import { flow, makeAutoObservable } from "mobx"
import { apiFetch, mediaUrl } from "../lib/api.js"
import type { RootStore } from "./RootStore.js"

interface GallerySummary {
  id: string
  slug: string
  title: string
  description: string | null
  coverMediaId: string | null
  visibility: string
  pricingType: string
  priceStars: number
  status: string
  ownerUserId: string
  mediaCount: number
  createdAt: string
  themeConfig?: string
}

interface MediaItem {
  id: string
  galleryId: string
  caption: string | null
  width: number | null
  height: number | null
  sortIndex: number
  proxyUrl: string
  createdAt: string
  uploader?: {
    id: string
    displayName: string
    username: string | null
    avatarUrl: string | null
  }
}

type GalleryListState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "loaded"; galleries: GallerySummary[] }
  | { status: "error"; message: string }

type GalleryDetailState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "loaded"; gallery: GallerySummary; media: MediaItem[] }
  | { status: "no_access"; gallery: GallerySummary }
  | { status: "error"; message: string }

type InboxState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "loaded"; items: MediaItem[]; galleryId: string; retentionHours: number }
  | { status: "error"; message: string }

interface Contributor {
  user: { id: string; displayName: string; username: string | null; avatarUrl: string | null }
  count: number
}

type ContributorsState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "loaded"; contributors: Contributor[] }
  | { status: "error"; message: string }

export class GalleryStore {
  listState: GalleryListState = { status: "idle" }
  detailState: GalleryDetailState = { status: "idle" }
  inboxState: InboxState = { status: "idle" }
  contributorsState: ContributorsState = { status: "idle" }
  rootStore: RootStore

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore
    makeAutoObservable(this, { rootStore: false })
  }

  fetchMyGalleries = flow(function* (this: GalleryStore) {
    this.listState = { status: "loading" }
    const result: Awaited<ReturnType<typeof apiFetch>> = yield apiFetch("/api/galleries/mine")
    if (result.status === "ok") {
      const data = result.data as { galleries: GallerySummary[] }
      this.listState = { status: "loaded", galleries: data.galleries }
    } else {
      this.listState = { status: "error", message: result.message }
    }
  })

  fetchGallery = flow(function* (this: GalleryStore, slug: string) {
    this.detailState = { status: "loading" }

    const galleryResult: Awaited<ReturnType<typeof apiFetch>> = yield apiFetch(
      `/api/galleries/${slug}`
    )
    if (galleryResult.status !== "ok") {
      this.detailState = { status: "error", message: galleryResult.message }
      return
    }

    const gallery = galleryResult.data as GallerySummary

    // Try to fetch media
    const mediaResult: Awaited<ReturnType<typeof apiFetch>> = yield apiFetch(
      `/api/galleries/${gallery.id}/media`
    )

    if (mediaResult.status === "ok") {
      const data = mediaResult.data as { items: MediaItem[]; total: number }
      this.detailState = {
        status: "loaded",
        gallery,
        media: data.items.map((item) => ({
          ...item,
          proxyUrl: mediaUrl(item.id),
        })),
      }
    } else {
      // No access
      this.detailState = { status: "no_access", gallery }
    }
  })

  createGallery = flow(function* (this: GalleryStore, title: string, description?: string) {
    const result: Awaited<ReturnType<typeof apiFetch>> = yield apiFetch("/api/galleries", {
      method: "POST",
      body: JSON.stringify({ title, description }),
    })
    if (result.status === "ok") {
      void this.fetchMyGalleries()
      return result.data as GallerySummary
    }
    return null
  })

  updateGallery = flow(function* (
    this: GalleryStore,
    id: string,
    data: Partial<{
      title: string
      description: string
      coverMediaId: string
      visibility: string
      pricingType: string
      priceStars: number
      themeConfig: string
    }>
  ) {
    const result: Awaited<ReturnType<typeof apiFetch>> = yield apiFetch(`/api/galleries/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    })
    if (result.status === "ok") {
      // Refresh both lists
      void this.fetchMyGalleries()
      if (this.detailState.status === "loaded" && this.detailState.gallery.id === id) {
        const updated = result.data as GallerySummary
        this.detailState = {
          ...this.detailState,
          gallery: { ...this.detailState.gallery, ...updated },
        }
      }
      return true
    }
    return false
  })

  deleteGallery = flow(function* (this: GalleryStore, id: string) {
    const result: Awaited<ReturnType<typeof apiFetch>> = yield apiFetch(`/api/galleries/${id}`, {
      method: "DELETE",
    })
    if (result.status === "ok") {
      void this.fetchMyGalleries()
      return true
    }
    return false
  })

  deleteMedia = flow(function* (this: GalleryStore, galleryId: string, mediaId: string) {
    const result: Awaited<ReturnType<typeof apiFetch>> = yield apiFetch(
      `/api/galleries/${galleryId}/media/${mediaId}`,
      { method: "DELETE" }
    )
    if (result.status === "ok") {
      if (this.detailState.status === "loaded") {
        this.detailState = {
          ...this.detailState,
          media: this.detailState.media.filter((m) => m.id !== mediaId),
        }
      }
      return true
    }
    return false
  })

  reorderMedia = flow(function* (this: GalleryStore, galleryId: string, mediaIds: string[]) {
    const result: Awaited<ReturnType<typeof apiFetch>> = yield apiFetch(
      `/api/galleries/${galleryId}/media/reorder`,
      { method: "PUT", body: JSON.stringify({ mediaIds }) }
    )
    if (result.status === "ok") {
      if (this.detailState.status === "loaded") {
        const mediaMap = new Map(this.detailState.media.map((m) => [m.id, m]))
        this.detailState = {
          ...this.detailState,
          media: mediaIds.map((id, i) => ({ ...mediaMap.get(id)!, sortIndex: i })),
        }
      }
      return true
    }
    return false
  })

  updateMediaCaption = flow(function* (this: GalleryStore, mediaId: string, caption: string) {
    const result: Awaited<ReturnType<typeof apiFetch>> = yield apiFetch(`/api/media/${mediaId}`, {
      method: "PUT",
      body: JSON.stringify({ caption }),
    })
    if (result.status === "ok") {
      if (this.detailState.status === "loaded") {
        this.detailState = {
          ...this.detailState,
          media: this.detailState.media.map((m) =>
            m.id === mediaId ? { ...m, caption } : m
          ),
        }
      }
      return true
    }
    return false
  })

  fetchPendingMedia = flow(function* (this: GalleryStore, galleryId: string) {
    const result: Awaited<ReturnType<typeof apiFetch>> = yield apiFetch(
      `/api/galleries/${galleryId}/media?status=pending`
    )
    if (result.status === "ok") {
      const data = result.data as { items: MediaItem[]; total: number }
      return data.items.map((item) => ({ ...item, proxyUrl: mediaUrl(item.id) }))
    }
    return []
  })

  fetchInbox = flow(function* (this: GalleryStore) {
    this.inboxState = { status: "loading" }
    const result: Awaited<ReturnType<typeof apiFetch>> = yield apiFetch("/api/galleries/inbox")
    if (result.status === "ok") {
      const data = result.data as {
        id: string
        items: MediaItem[]
        mediaCount: number
        retentionHours: number
      }
      this.inboxState = {
        status: "loaded",
        items: data.items.map((item) => ({ ...item, proxyUrl: mediaUrl(item.id) })),
        galleryId: data.id,
        retentionHours: data.retentionHours,
      }
    } else {
      this.inboxState = { status: "error", message: result.message }
    }
  })

  moveMedia = flow(function* (
    this: GalleryStore,
    mediaIds: string[],
    targetGalleryId: string
  ) {
    const result: Awaited<ReturnType<typeof apiFetch>> = yield apiFetch("/api/galleries/move-media", {
      method: "POST",
      body: JSON.stringify({ mediaIds, targetGalleryId }),
    })
    if (result.status === "ok") {
      // Remove moved items from inbox state
      if (this.inboxState.status === "loaded") {
        this.inboxState = {
          ...this.inboxState,
          items: this.inboxState.items.filter((m) => !mediaIds.includes(m.id)),
        }
      }
      void this.fetchMyGalleries()
      return true
    }
    return false
  })

  approveMedia = flow(function* (this: GalleryStore, mediaId: string) {
    const result: Awaited<ReturnType<typeof apiFetch>> = yield apiFetch(
      `/api/media/${mediaId}/approve`,
      { method: "POST" }
    )
    return result.status === "ok"
  })

  rejectMedia = flow(function* (this: GalleryStore, mediaId: string) {
    const result: Awaited<ReturnType<typeof apiFetch>> = yield apiFetch(
      `/api/media/${mediaId}/reject`,
      { method: "POST" }
    )
    return result.status === "ok"
  })

  fetchContributors = flow(function* (this: GalleryStore, galleryId: string) {
    this.contributorsState = { status: "loading" }
    const result: Awaited<ReturnType<typeof apiFetch>> = yield apiFetch(
      `/api/galleries/${galleryId}/contributors`
    )
    if (result.status === "ok") {
      const data = result.data as { contributors: Contributor[] }
      this.contributorsState = { status: "loaded", contributors: data.contributors }
    } else {
      this.contributorsState = { status: "error", message: result.message }
    }
  })

  removeContributor = flow(function* (this: GalleryStore, galleryId: string, userId: string) {
    const result: Awaited<ReturnType<typeof apiFetch>> = yield apiFetch(
      `/api/galleries/${galleryId}/contributors/${userId}`,
      { method: "DELETE" }
    )
    if (result.status === "ok") {
      const data = result.data as { contributors: Contributor[] }
      this.contributorsState = { status: "loaded", contributors: data.contributors }
      return true
    }
    return false
  })
}
